import { readAuthUserRaw } from './api-config';
import type { User } from '../types/config';

const PORTAL_ASSIGNMENT_ENDPOINTS: Array<{ method: string; path: string }> = [
  { method: 'GET', path: '/assignments/submissions' },
  { method: 'POST', path: '/assignments/submissions' },
];

function normalizePath(endpoint: string): string {
  return endpoint.split('?')[0].split('#')[0];
}

export function isPortalAssignmentSubmitEndpoint(method: string, endpoint: string): boolean {
  const path = normalizePath(endpoint);
  const verb = method.toUpperCase();
  if (path.startsWith('/assignments/submissions/grade')) return false;
  return PORTAL_ASSIGNMENT_ENDPOINTS.some((rule) => rule.method === verb && path.startsWith(rule.path));
}

function readStoredUser(): User | null {
  const raw = readAuthUserRaw();
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

/** True when the session may submit or view own homework from the portal. */
export function portalUserHasAssignmentSubmitAccess(): boolean {
  const user = readStoredUser();
  if (!user) return false;

  const perms = user.permissions ?? [];
  if (
    perms.includes('assignment:submit') ||
    perms.includes('assignment:grade') ||
    perms.includes('assignment:read')
  ) {
    return true;
  }

  const portal = user.moduleAccess?.find((entry) => {
    const code = entry.module_code.trim().toLowerCase();
    return code === 'portal';
  });
  if (!portal || portal.enabled === false) return false;
  if (portal.access_level === 'write') return true;
  return (portal.submodules ?? []).some(
    (sub) =>
      (sub.submodule_id === 'submit_assignments' ||
        sub.display_name?.toLowerCase().includes('submit')) &&
      sub.access_level === 'write'
  );
}

/**
 * Override explicit client-side api-access denials for portal homework submit/list.
 * Backend still enforces identity (own student) and enrollment scope.
 */
export function shouldAllowPortalAssignmentSubmit(method: string, endpoint: string): boolean {
  if (!isPortalAssignmentSubmitEndpoint(method, endpoint)) return false;
  return portalUserHasAssignmentSubmitAccess();
}
